import React from 'react'
import styled from 'styled-components'
import { Link, useNavigate } from 'react-router-dom'

import th from 'utils/themeHelper'
import SmallButtonPrimary from '../SmallButtonPrimary/SmallButtonPrimary'
import LinkButton from '../SmallButtonPrimary/LinkButton'

import { CheckCircle, Supporters } from '../Icons/Icons'

import { OpenBook } from '@styled-icons/entypo/OpenBook'
import { Number } from '@styled-icons/octicons/Number'
import { PencilRuler } from '@styled-icons/remix-line/PencilRuler'
import { BookLetter } from '@styled-icons/fluentui-system-filled/BookLetter'
import { ReactComponent as OppIcon } from 'assets/images/svgs/icon-campaign-causes-total.svg'
import { ReactComponent as Calendar } from 'assets/images/icons/opp-calendar.svg'

import l from 'config/localization'
import constants from 'config/constants'

const deriveIcon = ({ subjectName }) => {
  if (subjectName === 'Mathematics') return Number
  if (subjectName === 'Literacy') return BookLetter
  if (subjectName === 'Story Writing') return PencilRuler

  return OpenBook
}

const Card = styled.div`
  background: white;
  padding: 15px;
  border-radius: 3px;
  border: 1px solid ${th('borders.default')};
  position: relative;

  @media screen and (min-width: 600px) {
    padding: 30px;
  }
`

const SubjectIconWrapper = styled.div`
  height: 100%;
  opacity: 0.35;
  padding: 50px;
  position: absolute;
  right: 0;
  top: 0;

  svg {
    height: 100%;
    width: 100%;
  }
`

const Heading = styled.h4`
  margin: 0;
`

const CalendarWrapper = styled.div`
  display: flex;
  margin-bottom: 15px;

  div {
    margin-top: 3px;
  }

  svg {
    height: 100%;
    margin-right: 10px;
    width: 20px;
  }
`

const ClosedEnrolmentsHint = styled.span`
  color: ${th('text.light')};
  font-family: 'ProximaNovaRegularIt';
  font-size: 12px;
  margin-left: 10px;
  margin-top: 5px;
`

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  @media screen and (max-width: 600px) {
    justify-content: space-between;
  }
`

const ActionLink = styled(Link)`
  text-decoration: none;
  margin-left: 15px;

  &:first-child {
    margin-left: 0;
  }

  @media screen and (max-width: 769px) {
    margin-left: 0;
    margin-bottom: 12px;
  }
`

const Info = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 30px 0;

  @media screen and (max-width: 769px) {
    flex-direction: column;
    margin: 15px 0;
  }
`

const InfoGroup = styled.div`
  flex: 1;
`

const LaunchWarning = styled.div`
  line-height: 1.5em;
  margin: 30px 0;
`

const StatusBoxWrapper = styled.div`
  background: ${props => (props.red ? '#EB452566' : '#a8db4366')};
  border: 1px solid ${props => (props.red ? '#EB4525' : '#a8db43')};
  border-radius: 4px;
  cursor: default;
  display: flex;
  font-size: 12px;
  font-family: ${th('fonts.bold')};
  padding: 10px 24px;

  svg {
    color: #212121;
    margin-left: -8px;
    margin-right: 10px;
    margin-top: -1px;
    width: 13px;
  }
`

const SubjectBoxContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and (min-width: 769px) {
    align-items: flex-end;
    flex: 2;
  }
`

const SubjectBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`

const SubjectBoxLabel = styled.div`
  color: #212121;
  font-family: ${th('fonts.bold')};
  margin-bottom: 8px;
`

const SubjectBox = styled.div`
  align-items: center;
  background: #ed992055;
  border: 1px solid #717171;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  flex: 1;
  height: 70px;
  justify-content: center;
  margin-bottom: 10px;
  min-width: 160px;
  padding: 8px 20px 8px 10px;
  text-align: center;

  &:hover {
    border-color: #ed9920;
    background: #ed992088;
  }

  svg {
    height: 20px;
    margin-right: 10px;
  }
`

const LaunchCompetitionButton = ({ campaign, campLaunched, status, toggleLaunchConfirmation }) => {
  const isProvCompetition = !campaign.parent_campaign_id
  if (!isProvCompetition) return null

  const isActiveStatus = status === constants.CAMPAIGN_STATUSES.ACTIVE
  const isActive = isActiveStatus || campLaunched

  if (!isActive) {
    return (
      <SmallButtonPrimary
        onClick={() => toggleLaunchConfirmation({ idForCampaignLaunch: campaign.id })}
      >
        Launch
      </SmallButtonPrimary>
    )
  }

  return (
    <StatusBoxWrapper>
      <CheckCircle />
      <span>Launched</span>
    </StatusBoxWrapper>
  )
}

const EnrolmentsOpenButton = ({ campaign, enrolmentsOpen }) => {
  const isProvCompetition = !campaign.parent_campaign_id
  if (isProvCompetition) return null

  return (
    <StatusBoxWrapper red={!enrolmentsOpen}>
      {enrolmentsOpen && <CheckCircle />}
      <span>{enrolmentsOpen ? 'Enrolments Open' : 'Enrolments closed'}</span>
    </StatusBoxWrapper>
  )
}

const RoundsSection = ({ isProvComp, numberOfRounds }) => {
  if (isProvComp) return null

  return (
    <CalendarWrapper>
      <OppIcon />
      <div>
        {numberOfRounds} {`${numberOfRounds === 1 ? 'round' : 'rounds'}`}
      </div>
    </CalendarWrapper>
  )
}

const GiantSubjectSection = ({ categories, isProvComp }) => {
  if (isProvComp) return null
  if (!categories.length) return null

  const subjectName = categories[0].name
  const SubjectIcon = deriveIcon({ subjectName })

  return (
    <SubjectIconWrapper>
      <SubjectIcon />
    </SubjectIconWrapper>
  )
}

const SubjectCompetitionsSection = ({ categories, navigate, isProvComp }) => {
  if (!isProvComp) return null

  return (
    <SubjectBoxContainer>
      <SubjectBoxLabel>Subjects available</SubjectBoxLabel>

      <SubjectBoxWrapper>
        {categories.map(subject => {
          const subjectName =
            subject.name === 'Story Writing and Poetry' ? 'Story Writing' : subject.name
          const SubjectIcon = deriveIcon({ subjectName })

          return (
            <SubjectBox
              key={subject.id}
              onClick={() =>
                navigate(`/dashboard/competitions?comptype=subject&s=${subject.campaign_name}`)
              }
            >
              <SubjectIcon />

              <div>{subjectName}</div>
            </SubjectBox>
          )
        })}
      </SubjectBoxWrapper>
    </SubjectBoxContainer>
  )
}

const SubjectsSection = ({ categories, isProvComp }) => {
  if (!isProvComp) return null

  return (
    <CalendarWrapper>
      <OpenBook />

      <div>
        {categories.length}{' '}
        {`${categories.length === 1 ? 'subject competition' : 'subject competitions'}`}
      </div>
    </CalendarWrapper>
  )
}

const DateSection = ({ startDate, endDate }) => {
  if (!endDate || !startDate) return null

  return (
    <CalendarWrapper>
      <Calendar />
      <div>
        {startDate} - {endDate}
      </div>
    </CalendarWrapper>
  )
}

const RegSection = ({ isProvComp, registrationCount }) => {
  if (isProvComp) return null

  return (
    <CalendarWrapper>
      <Supporters />
      <div>{registrationCount} registrations</div>
    </CalendarWrapper>
  )
}

const LearnerSection = ({ enrolmentsOpen, isProvComp, supporterCount }) => {
  return (
    <div>
      <CalendarWrapper>
        <Supporters />
        <div>{supporterCount || 0} learners</div>

        {!enrolmentsOpen && !isProvComp && (
          <ClosedEnrolmentsHint>(enrolments closed)</ClosedEnrolmentsHint>
        )}
      </CalendarWrapper>
    </div>
  )
}

const CampaignCard = ({
  categories = [],
  startDate,
  endDate,
  enrolmentsOpen,
  numberOfRounds,
  isProvComp,
  registrationCount,
  supporterCount,
  launchComp,
  idForCampaignLaunch,
  campLaunching,
  campLaunched,
  toggleLaunchConfirmation,
  campaign = {}
}) => {
  const { id, title, status } = campaign
  const isPrivateCamp = campaign.is_private_campaign
  const pathToCamp = `/${l('ROUTE_CAMPS')}/${id}?title=${campaign.short_url}`
  const shareableUrl = isPrivateCamp
    ? `${pathToCamp}&pvtShareKey=${campaign.pvt_share_key}`
    : pathToCamp

  if (idForCampaignLaunch && id === idForCampaignLaunch) {
    return (
      <Card>
        <Heading>{title}</Heading>

        <LaunchWarning>
          Please confirm that you want to set this competition to be active.
        </LaunchWarning>

        <Actions>
          <SmallButtonPrimary onClick={() => launchComp({ campaignId: id })}>
            Yes, launch competition
          </SmallButtonPrimary>

          <div style={{ marginLeft: '15px' }}>
            <LinkButton onClick={() => toggleLaunchConfirmation({ idForCampaignLaunch: null })}>
              Cancel
            </LinkButton>
          </div>
        </Actions>
      </Card>
    )
  }

  const navigate = useNavigate()

  return (
    <Card>
      <Heading>{title}</Heading>

      <Info>
        <InfoGroup>
          <DateSection startDate={startDate} endDate={endDate} />
          <SubjectsSection categories={categories} isProvComp={isProvComp} />
          <RoundsSection isProvComp={isProvComp} numberOfRounds={numberOfRounds} />
          <RegSection isProvComp={isProvComp} registrationCount={registrationCount} />
          <LearnerSection
            enrolmentsOpen={enrolmentsOpen}
            isProvComp={isProvComp}
            supporterCount={supporterCount}
          />
        </InfoGroup>

        <SubjectCompetitionsSection
          categories={categories}
          isProvComp={isProvComp}
          navigate={navigate}
        />
      </Info>

      <GiantSubjectSection categories={categories} isProvComp={isProvComp} />

      {campLaunching ? (
        <Actions>Busy launching {l('CAMPS')}</Actions>
      ) : (
        <Actions>
          <LaunchCompetitionButton
            campaign={campaign}
            campLaunched={campLaunched}
            status={status}
            toggleLaunchConfirmation={toggleLaunchConfirmation}
          />

          <EnrolmentsOpenButton campaign={campaign} enrolmentsOpen={enrolmentsOpen} />

          <ActionLink to={shareableUrl}>
            <LinkButton>View</LinkButton>
          </ActionLink>

          <ActionLink to={`/dashboard/${l('ROUTE_CAMPS')}/${id}`}>
            <LinkButton>Edit</LinkButton>
          </ActionLink>

          <div style={{ marginLeft: '15px' }}>
            <LinkButton
              onClick={() =>
                navigate(
                  `/dashboard/${l('ROUTE_CAMPS')}/${id}/${
                    campaign.parent_campaign_id ? 'competition-schools' : 'province-schools'
                  }`
                )
              }
            >
              Schools
            </LinkButton>
          </div>

          {campaign.parent_campaign_id && (
            <div style={{ marginLeft: '15px' }}>
              <LinkButton onClick={() => navigate(`/dashboard/rounds?cid=${campaign.id}&page=1`)}>
                Rounds
              </LinkButton>
            </div>
          )}

          {campaign.requiresHardcopySubmission && (
            <div style={{ marginLeft: '15px' }}>
              <LinkButton
                onClick={() =>
                  navigate(`/dashboard/${l('ROUTE_CAMPS')}/${id}/hardcopy-submissions`)
                }
              >
                Hardcopy Submissions
              </LinkButton>
            </div>
          )}
        </Actions>
      )}
    </Card>
  )
}

export default CampaignCard
